import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { noop } from 'lodash';
import classNames from 'classnames';
import { getPlacementByShape, getUiState } from 'state/ads/selectors';
import { getScreenName } from 'state/screen/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { AdKind, AdPage, AdPlatform } from 'constants/ads';
import { UiState } from 'constants/ui';
import { Screen } from 'constants/tracking/screens';
import useAbTest from 'hooks/useAbTest';
import AdManager from 'libs/common/ad-manager';
import AdContent from './AdContent';
import AdPlaceholder from './AdPlaceholder';
import AdErrorBoundary from './AdErrorBoundary';
import useStickyOptions from './useStickyOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

function useBlockthroughMediation() {
  // This utility is temporary, just to separate A/B test
  // related logic from the Advertisement component.
  //
  // TODO: Clean this up
  // When scaling web_adblock_monetisation (AB test)
  // And/or web_blockthrough_ads_script (Feature switch)
  const screenName = useSelector(getScreenName);
  const isBlockthroughScriptEnabled = useSelector(getIsFeatureSwitchEnabled('web_ads_blockthrough_script'));
  const isAdsPlatformWeb = AdManager.getAdsPlatform() === AdPlatform.Web;
  const isBlockthroughAbTestPage = [Screen.ConversationList, Screen.ConversationNew, Screen.ConversationReply, Screen.Catalog, Screen.Item].includes(screenName);
  const isBlockthroughAbTestEnabled = isBlockthroughScriptEnabled && isAdsPlatformWeb && isBlockthroughAbTestPage;
  const adblockMonetisationAbTest = useAbTest({
    abTestName: 'web_adblock_monetisation'
  });
  if (!isBlockthroughAbTestEnabled || !adblockMonetisationAbTest || adblockMonetisationAbTest.variant === 'off') return null;
  return "bt-test-".concat(adblockMonetisationAbTest.variant);
}

const Advertisement = _ref => {
  var _ref$current;

  let {
    id: propsId,
    shape,
    railsComponent,
    mediation = null,
    config,
    isManuallyRendered = false,
    isManuallyRefreshed = false,
    onAdRender = noop
  } = _ref;
  const ref = useRef(null);
  const {
    0: isAdRendered,
    1: setIsAdRendered
  } = useState(false);
  const blockthroughMediation = useBlockthroughMediation();
  const uiState = useSelector(getUiState);
  const placement = useSelector(getPlacementByShape(shape, blockthroughMediation || mediation));
  const id = useMemo(() => propsId || AdManager.generatePlacementId(), [propsId]);
  const placementConfig = useMemo(() => config || placement, [config, placement]);
  const stickyOptions = useStickyOptions({
    isSticky: placementConfig === null || placementConfig === void 0 ? void 0 : placementConfig.options.isSticky,
    isRailsComponent: railsComponent,
    parentElement: (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.parentElement
  });
  const handleAdRender = useCallback(isAdVisible => {
    onAdRender(isAdVisible);
    setIsAdRendered(isAdVisible);
  }, [onAdRender]);
  if (!placementConfig || uiState !== UiState.Idle) return null;
  return /*#__PURE__*/_jsx(AdErrorBoundary, {
    id: id,
    pageName: placementConfig.page || AdPage.Unknown,
    kind: placementConfig.kind,
    dfpCode: placementConfig.kind === AdKind.Rtb ? placementConfig.dfpCode : placementConfig.code,
    children: /*#__PURE__*/_jsxs("div", {
      className: classNames('ad-container', "ad-container--".concat(shape), !!stickyOptions && !railsComponent && 'ad-sticky', isAdRendered && 'ad-container--rendered'),
      "data-testid": "advertisement",
      style: railsComponent ? undefined : {
        top: stickyOptions === null || stickyOptions === void 0 ? void 0 : stickyOptions.offset
      },
      ref: ref,
      children: [/*#__PURE__*/_jsx(AdPlaceholder, {
        shape: shape
      }), /*#__PURE__*/_jsx(AdContent, {
        id: id,
        placementConfig: placementConfig,
        isManuallyRefreshed: isManuallyRefreshed,
        isManuallyRendered: isManuallyRendered,
        onAdRender: handleAdRender
      })]
    })
  });
};

export default Advertisement;